import { getField, updateField } from 'vuex-map-fields'
import api from '@/lib/api'
import { NOT_STARTED, IN_PROGRESS, COMPLETE, ERROR } from '@/lib/constants/actionStatuses'
import { PATH_WORKFLOW_TASKS } from '@/lib/constants/api'
import getWorkflowTaskModel from './models/workflowTask'
import updateStatus from '@/store/helpers/updateStatus'
import { notifySuccess, notifyError } from '@/lib/notifications'
import { hideProgressModal, showProgressModal } from '@/lib/progressModal'

const workflowTask = {
  namespaced: true,
  state: {
    ...getWorkflowTaskModel(),
    notes: []
  },
  getters: {
    getField
  },
  mutations: {
    updateField,
    updateStatus,
    clearWorkflowTask: (state) => {
      const model = getWorkflowTaskModel()
      Object.keys(model).forEach(key => { state[key] = model[key] })
      state.notes = []
    },
    setWorkflowTask: (state, task) => {
      const model = getWorkflowTaskModel()
      Object.keys(model).forEach(key => { state[key] = task[key] !== undefined ? task[key] : model[key] })
    },
    setNotes: (state, notes) => {
      state.notes = notes || []
    }
  },
  actions: {
    getWorkflowTask: ({ commit, dispatch }, { id }) => {
      commit('updateStatus', { status: IN_PROGRESS })
      showProgressModal({ dispatch, message: 'Loading task, please wait...' })

      return api.doGet({ path: `${PATH_WORKFLOW_TASKS}/${id}` })
        .then(json => json.data)
        .then(task => {
          commit('setWorkflowTask', task)
          commit('updateStatus', { status: COMPLETE })
          return task
        }).catch((error) => {
          commit('updateStatus', { status: ERROR, error })
        })
        .finally(() => {
          hideProgressModal({ dispatch })
        })
    },
    getNotes: ({ commit }, taskId) => {
      return api.doGet({ path: `${PATH_WORKFLOW_TASKS}/${taskId}/notes` })
        .then(json => json.data)
        .then(notes => {
          commit('setNotes', notes)
          return notes
        })
    },
    update: ({ state }) => {
      const { id, statusId, assignedToUserId, dueDate, title, description, blockerTaskId, sortOrder, workflowInstanceId } = state
      return api.doPut({
        path: PATH_WORKFLOW_TASKS,
        data: { id, statusId, assignedToUserId, dueDate, title, description, blockerTaskId, sortOrder, workflowInstanceId }
      })
    },
    quickUpdateStatus: (context, { task, statusId }) => {
      return api.doPut({
        path: PATH_WORKFLOW_TASKS,
        data: { ...task, statusId }
      })
    },
    addNote: (context, { taskId, note }) => {
      return api.doPost({ path: `${PATH_WORKFLOW_TASKS}/${taskId}/notes`, data: { note } })
    },
    saveWorkflowTask: ({ state, commit, dispatch }) => {
      commit('updateStatus', { status: IN_PROGRESS })
      showProgressModal({ dispatch, message: 'Saving task, please wait...' })

      return dispatch('update').then(response => {
        if (!response.data) {
          throw new Error(response.errorMessage || 'Unable to save task. Check required fields.')
        }

        commit('setWorkflowTask', response.data)
        commit('updateStatus', { status: COMPLETE })
        notifySuccess(dispatch, 'Task saved')
        return response.data
      }).catch((error) => {
        commit('updateStatus', { status: ERROR, error })
        notifyError(dispatch, error || 'Unable to save task. Try again later')
        throw error
      })
        .finally(() => {
          hideProgressModal({ dispatch })
        })
    },
    addTaskNote: ({ commit, dispatch }, { taskId, note }) => {
      showProgressModal({ dispatch, message: 'Adding note, please wait...' })

      return dispatch('addNote', { taskId, note }).then(response => {
        if (!response.data) {
          throw new Error(response.errorMessage || 'Unable to add note.')
        }

        notifySuccess(dispatch, 'Note added')
        return dispatch('getNotes', taskId)
      }).catch((error) => {
        notifyError(dispatch, error || 'Unable to add note. Try again later')
        throw error
      })
        .finally(() => {
          hideProgressModal({ dispatch })
        })
    },
    clearWorkflowTask: ({ commit }) => {
      commit('clearWorkflowTask')
      return commit('updateStatus', { status: NOT_STARTED })
    },
    reExecuteTask: ({ dispatch }, taskId) => {
      showProgressModal({ dispatch, message: 'Re-executing task, please wait...' })

      return api.doPost({ path: `${PATH_WORKFLOW_TASKS}/${taskId}/execute` })
        .then(response => {
          if (!response.data) {
            throw new Error(response.errorMessage || 'Unable to re-execute task.')
          }

          notifySuccess(dispatch, 'Task re-executed')
          return response.data
        }).catch((error) => {
          notifyError(dispatch, error || 'Unable to re-execute task. Try again later')
          throw error
        })
        .finally(() => {
          hideProgressModal({ dispatch })
        })
    }
  }
}

export default workflowTask
